import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { Task } from "@lit/task";
import { fetchApi, ApiError, types } from "../api/index.js";
import { displayCanonicalWithTags, variantOf, streamOf } from "../utils/canonical.js";
import { shortDate, shortDigest, apiLink } from "../utils/format.js";
import "../components/loading-spinner.js";
import "../components/error-message.js";

export type DiffStatus = "added" | "removed" | "changed";

export interface PackageDiff {
  status: DiffStatus;
  name: string;
  evrOld: string;
  evrNew: string;
}

/** Compute the package diff between an old and new SBOM. */
export function diffPackages(oldPkgs: types.SbomPackage[], newPkgs: types.SbomPackage[]): PackageDiff[] {
  const oldMap = new Map<string, types.SbomPackage>();
  for (const p of oldPkgs) oldMap.set(p.name, p);
  const newMap = new Map<string, types.SbomPackage>();
  for (const p of newPkgs) newMap.set(p.name, p);

  const result: PackageDiff[] = [];
  for (const [name, pkg] of newMap) {
    const old = oldMap.get(name);
    if (!old) {
      result.push({ status: "added", name, evrOld: "", evrNew: pkg.evr });
    } else if (old.evr !== pkg.evr) {
      result.push({ status: "changed", name, evrOld: old.evr, evrNew: pkg.evr });
    }
  }
  for (const [name, pkg] of oldMap) {
    if (!newMap.has(name)) result.push({ status: "removed", name, evrOld: pkg.evr, evrNew: "" });
  }
  return result.sort((a, b) => a.name.localeCompare(b.name));
}

/** Extract packages for a given architecture from an SbomResponse (excludes source RPMs). */
function archPackages(data: types.SbomResponse, arch: string): types.SbomPackage[] {
  const archs = Object.keys(data);
  const pkgs = data[arch]?.packages ?? data[archs[0]]?.packages ?? [];
  return pkgs.filter((p) => p.category !== "source");
}

/** Get the package count for a given architecture (runtime + build, excludes source). */
function archPackageCount(data: types.SbomResponse, arch: string): number {
  const archs = Object.keys(data);
  const a = data[arch] ?? data[archs[0]];
  return (a?.runtime_count ?? 0) + (a?.build_count ?? 0);
}

type SortCol = "status" | "name" | "evrOld" | "evrNew";

@customElement("comparison-tab")
export class ComparisonTab extends LitElement {
  @property() imageName = "";
  @property() canonical = "";
  @property({ type: Array }) canonicals: string[] = [];
  @property({ type: Array }) tags: types.Tag[] = [];
  @state() private _selectedArch = "";
  @state() private _selectedDigest = "";
  @state() private _filter = "";
  @state() private _sortCol: SortCol = "name";
  @state() private _sortAsc = true;

  createRenderRoot() {
    return this;
  }

  /** Stream of the currently selected canonical. */
  private get _currentStream(): string {
    return streamOf(this.canonical, this.tags);
  }

  /** Variant of the currently selected canonical. */
  private get _currentVariant(): string {
    return variantOf(this.canonical, this.tags);
  }

  /** Hex digest (without sha256: prefix) of the selected previous release. */
  private get _compareHex(): string {
    const digest = this._effectiveDigest;
    return digest ? digest.replace("sha256:", "") : "";
  }

  /** The effective digest to compare against: user-selected or auto-selected (second release). */
  private get _effectiveDigest(): string {
    const releases = this._historyTask.value?.releases;
    if (!releases || releases.length < 2) return "";
    const previous = releases.slice(1);
    if (this._selectedDigest && previous.some((r) => r.digest === this._selectedDigest)) return this._selectedDigest;
    return previous[0].digest;
  }

  /** Active architecture: user-selected or first available from the new SBOM data. */
  private get _activeArch(): string {
    const data = this._taskNew.value;
    const archs = data ? Object.keys(data) : [];
    if (data && this._selectedArch && archs.includes(this._selectedArch)) {
      return this._selectedArch;
    }
    return archs[0] ?? "amd64";
  }

  private _historyTask = new Task(this, {
    args: () => [this.imageName, this._currentStream, this._currentVariant] as const,
    task: ([name, stream, variant]) =>
      stream && variant ? fetchApi<types.HistoryResponse>(`/v1/images/${name}/history/${stream}/${variant}`) : Promise.reject("No stream/variant"),
  });

  private _taskNew = new Task(this, {
    args: () => [this.imageName, this.canonical] as const,
    task: ([name, canonical]) =>
      canonical ? fetchApi<types.SbomResponse>(`/v1/images/${name}/sbom/${canonical}`) : Promise.reject("No canonical"),
  });

  private _taskOld = new Task(this, {
    args: () => [this.imageName, this._compareHex] as const,
    task: ([name, hex]) =>
      hex ? fetchApi<types.SbomResponse>(`/v1/images/${name}/releases/sbom/${hex}`) : Promise.reject("No release"),
  });

  private _sorted(diffs: PackageDiff[]): PackageDiff[] {
    let filtered = diffs;
    if (this._filter) {
      const q = this._filter.toLowerCase();
      filtered = diffs.filter((d) => d.name.toLowerCase().includes(q));
    }
    const col = this._sortCol;
    const dir = this._sortAsc ? 1 : -1;
    return [...filtered].sort((a, b) => a[col].localeCompare(b[col]) * dir);
  }

  private _toggleSort(col: SortCol) {
    if (this._sortCol === col) {
      this._sortAsc = !this._sortAsc;
    } else {
      this._sortCol = col;
      this._sortAsc = true;
    }
  }

  private _sortIcon(col: SortCol) {
    if (this._sortCol !== col) return "";
    return this._sortAsc ? " \u2191" : " \u2193";
  }

  private _onCanonicalChange(e: Event) {
    this._selectedDigest = "";
    this.dispatchEvent(new CustomEvent("canonical-change", { detail: (e.target as HTMLSelectElement).value }));
  }

  private _onDigestChange(e: Event) {
    this._selectedDigest = (e.target as HTMLSelectElement).value;
  }

  private _onArchChange(e: Event) {
    this._selectedArch = (e.target as HTMLSelectElement).value;
  }

  private _releaseLabel(rel: types.HistorySummary): string {
    return `${shortDate(rel.oldest_created)} (${shortDigest(rel.digest)})`;
  }

  private _statusBadge(status: DiffStatus) {
    switch (status) {
      case "added":
        return html`<span class="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-900/50 text-green-400 border border-green-700/50">ADDED</span>`;
      case "removed":
        return html`<span class="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-900/50 text-red-400 border border-red-700/50">REMOVED</span>`;
      case "changed":
        return html`<span class="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-yellow-900/50 text-yellow-400 border border-yellow-700/50">CHANGED</span>`;
    }
  }

  private _rowClass(status: DiffStatus) {
    switch (status) {
      case "added":
        return "border-b border-hb-border/50 hover:bg-hb-surface/30 bg-green-950/20";
      case "removed":
        return "border-b border-hb-border/50 hover:bg-hb-surface/30 bg-red-950/20";
      case "changed":
        return "border-b border-hb-border/50 hover:bg-hb-surface/30 bg-yellow-950/10";
    }
  }

  render() {
    return html`
      <div class="mb-4 flex flex-wrap items-center gap-3">
        <div>
          <label class="text-gray-500 text-sm mr-1">Tag:</label>
          <select
            .value=${this.canonical}
            @change=${this._onCanonicalChange}
            class="bg-hb-surface border border-hb-border rounded px-2 py-1 text-sm text-gray-200"
          >
            ${this.canonicals.map((c) => html`<option value=${c} ?selected=${c === this.canonical}>${displayCanonicalWithTags(c, this.tags)}</option>`)}
          </select>
        </div>
        ${this._renderPreviousVersionDropdown()}
        ${this._renderArchSelect()}
        <div class="ml-auto">
          ${this.canonical ? apiLink(`/v1/images/${this.imageName}/sbom/${this.canonical}`) : ""}
        </div>
      </div>

      ${this._renderBody()}
    `;
  }

  private _renderArchSelect() {
    const data = this._taskNew.value;
    if (!data || Object.keys(data).length <= 1) return "";
    const active = this._activeArch;
    return html`
      <div>
        <label class="text-gray-500 text-sm mr-1">Arch:</label>
        <select
          .value=${active}
          @change=${this._onArchChange}
          class="bg-hb-surface border border-hb-border rounded px-2 py-1 text-sm text-gray-200"
        >
          ${Object.keys(data).map((a) => html`<option value=${a} ?selected=${a === active}>${a}</option>`)}
        </select>
      </div>
    `;
  }

  private _renderPreviousVersionDropdown() {
    if (!this._historyTask.value) {
      return html`
        <div>
          <label class="text-gray-500 text-sm mr-1">Previous version:</label>
          <select disabled class="bg-hb-surface border border-hb-border rounded px-2 py-1 text-sm text-gray-500">
            <option>Loading...</option>
          </select>
        </div>
      `;
    }
    const previous = (this._historyTask.value.releases ?? []).slice(1);
    const effective = this._effectiveDigest;
    return html`
      <div>
        <label class="text-gray-500 text-sm mr-1">Previous version:</label>
        <select
          .value=${effective}
          @change=${this._onDigestChange}
          class="bg-hb-surface border border-hb-border rounded px-2 py-1 text-sm text-gray-200"
          ?disabled=${!previous.length}
        >
          ${previous.length
            ? previous.map(
                (r) => html`<option value=${r.digest} ?selected=${r.digest === effective}>${this._releaseLabel(r)}</option>`,
              )
            : html`<option>No previous releases</option>`}
        </select>
      </div>
    `;
  }

  private _renderBody() {
    return this._historyTask.render({
      pending: () => html`<loading-spinner></loading-spinner>`,
      error: (e) =>
        e instanceof ApiError && e.code === "not_found"
          ? html`<div class="rounded-lg border border-hb-border bg-hb-surface/50 p-8 text-center">
              <p class="text-gray-400 text-sm">No release history available yet. History will appear here after the next data sync.</p>
            </div>`
          : html`<error-message .message=${String(e)}></error-message>`,
      complete: (data) => {
        if (data.releases.length < 2) {
          return html`
            <div class="rounded-lg border border-hb-border bg-hb-surface/50 p-8 text-center">
              <p class="text-gray-400 text-sm">No previous releases available for comparison.</p>
              <p class="text-gray-500 text-xs mt-1">A comparison will be available after the next release update.</p>
            </div>
          `;
        }
        return this._renderDiff();
      },
    });
  }

  private _renderDiff() {
    const newStatus = this._taskNew.status;
    const oldStatus = this._taskOld.status;

    if (newStatus === 1 || oldStatus === 1) return html`<loading-spinner></loading-spinner>`;
    if (newStatus === 3) return html`<error-message .message=${"Failed to load SBOM for current version"}></error-message>`;
    if (oldStatus === 3)
      return html`<error-message .message=${"Failed to load SBOM for previous version"}></error-message>`;

    const newData = this._taskNew.value;
    const oldData = this._taskOld.value;
    if (!newData || !oldData) return html`<loading-spinner></loading-spinner>`;

    const arch = this._activeArch;
    const newPkgs = archPackages(newData, arch);
    const oldPkgs = archPackages(oldData, arch);
    const newCount = archPackageCount(newData, arch);
    const oldCount = archPackageCount(oldData, arch);

    const diffs = diffPackages(oldPkgs, newPkgs);
    if (!diffs.length) {
      return html`
        <div class="rounded-lg border border-hb-border bg-hb-surface/50 p-8 text-center">
          <p class="text-gray-400 text-sm">No package differences found between these releases.</p>
          <p class="text-gray-500 text-xs mt-1">${newCount} packages in both releases</p>
        </div>
      `;
    }

    const added = diffs.filter((d) => d.status === "added").length;
    const removed = diffs.filter((d) => d.status === "removed").length;
    const changed = diffs.filter((d) => d.status === "changed").length;
    const sorted = this._sorted(diffs);

    return html`
      <div class="flex items-center justify-between mb-3">
        <span class="text-gray-400 text-sm">
          <span class="text-green-400 font-bold">${added}</span> added,
          <span class="text-red-400 font-bold">${removed}</span> removed,
          <span class="text-yellow-400 font-bold">${changed}</span> changed
          <span class="text-gray-500 ml-1">(of ${newCount} / ${oldCount} total)</span>
        </span>
        <input
          type="text"
          placeholder="Filter packages..."
          .value=${this._filter}
          @input=${(e: Event) => (this._filter = (e.target as HTMLInputElement).value)}
          class="bg-hb-surface border border-hb-border rounded px-2 py-1 text-xs text-gray-200
                 placeholder:text-gray-500 focus:outline-none focus:border-hb-accent w-40"
        />
      </div>

      <div class="border border-hb-border rounded-lg overflow-x-auto">
        <table class="w-full text-left text-xs">
          <thead class="bg-hb-surface text-gray-400">
            <tr>
              <th class="py-2 px-3 cursor-pointer hover:text-gray-200 w-24" @click=${() => this._toggleSort("status")}>
                Status${this._sortIcon("status")}
              </th>
              <th class="py-2 px-3 cursor-pointer hover:text-gray-200" @click=${() => this._toggleSort("name")}>
                Name${this._sortIcon("name")}
              </th>
              <th class="py-2 px-3 cursor-pointer hover:text-gray-200" @click=${() => this._toggleSort("evrOld")}>
                EVR (old)${this._sortIcon("evrOld")}
              </th>
              <th class="py-2 px-3 cursor-pointer hover:text-gray-200" @click=${() => this._toggleSort("evrNew")}>
                EVR (new)${this._sortIcon("evrNew")}
              </th>
            </tr>
          </thead>
          <tbody>
            ${sorted.map(
              (d) => html`
                <tr class=${this._rowClass(d.status)}>
                  <td class="py-1.5 px-3">${this._statusBadge(d.status)}</td>
                  <td class="py-1.5 px-3 text-gray-200 font-mono">${d.name}</td>
                  <td class="py-1.5 px-3 text-gray-400 font-mono">${d.evrOld || "\u2014"}</td>
                  <td class="py-1.5 px-3 text-gray-400 font-mono">${d.evrNew || "\u2014"}</td>
                </tr>
              `,
            )}
          </tbody>
        </table>
      </div>
      ${this._filter
        ? html`<p class="text-gray-500 text-xs mt-2">Showing ${sorted.length} of ${diffs.length} differences</p>`
        : ""}
    `;
  }
}
